import React from 'react';
import styled, { css } from 'styled-components';
import { useIntl } from 'react-intl-next';
import { SEARCH_INPUT_FONT_SIZE } from 'view/common/styled';
import * as grid from 'view/styles/grid';
import * as mixins from 'view/styles/mixins';
import * as transitions from 'view/styles/transitions';
import { searchTextbox } from 'view/styles/z-index';
import SearchIcon from '@atlaskit/icon/glyph/search';
import { Box, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { UnstyledButton } from '@atlassian/help-center-common-component/unstyled-button';
import searchBarMessages from '../search-bar/messages';
import messages from './messages';

export interface Props {
    id?: string;
    searchBarShown: boolean;
    term: string;
    onFocus?: () => void;
}

export const SearchTextbox = React.forwardRef((props: Props, ref: React.RefObject<HTMLInputElement>) => {
    const { term, searchBarShown, onFocus, id: searchBarId = '' } = props;
    const { formatMessage } = useIntl();

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== 'Tab' && onFocus) {
            onFocus();
        }
    };

    return (
        <>
            {searchBarShown && (
                <Box
                    xcss={xcss({
                        position: 'relative',
                    })}
                    id={searchBarId}
                >
                    <Input
                        type="text"
                        name={formatMessage(messages.inputLabel)}
                        aria-label={formatMessage(searchBarMessages.inputPlaceholder)}
                        autoComplete="off"
                        spellCheck={false}
                        placeholder={formatMessage(searchBarMessages.inputPlaceholder)}
                        defaultValue={term}
                        onMouseDown={onFocus}
                        onKeyDown={handleKeyDown}
                        innerRef={ref}
                    />
                    <IconButton type="submit" actionSubjectId="search">
                        <SearchIcon
                            primaryColor={token('color.icon', colors.N200)}
                            label={formatMessage(messages.searchButton)}
                        />
                    </IconButton>
                </Box>
            )}
        </>
    );
});

SearchTextbox.displayName = 'SearchTextbox';

const borderRadius = css`
    border-radius: 5px;
`;

const Input = styled.input`
    ${SEARCH_INPUT_FONT_SIZE};
    ${borderRadius};
    ${mixins.placeholder(token('color.text.subtlest', colors.N200))};
    padding: ${grid.multiple(2.125).px};
    padding-right: ${token('space.400', '32px')};
    width: 100%;
    border: 0;
    outline: none;
    position: relative;
    z-index: ${searchTextbox};
`;

const IconButton = styled(UnstyledButton)`
    position: absolute;
    padding: 0 ${token('space.200', '16px')};
    right: 0;
    top: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    border-radius: 0 ${mixins.borderRadiusValue} ${mixins.borderRadiusValue} 0;
    cursor: pointer;
    z-index: ${searchTextbox};

    &:hover {
        > span {
            ${transitions.zoom()};
        }
    }
`;
